import React, { useMemo, useCallback, useState, MouseEvent, useContext } from 'react';
import { Column } from 'react-table';
import { generatePath, useHistory, useLocation } from 'react-router-dom';
import { Button, Dropdown, message, Menu, Modal, Tag, Select } from 'antd';
import * as Icons from '@ant-design/icons';

import { StopPropagation } from '@core/ui';
import { CountryCell, OverCell, tableColumns } from '@core/table';
import { useService } from '@core/inversify-react';

import { ShopsTableContext } from '../../contexts';
import { IRemoveShopRepo, RemoveShopRepoType } from '../../repos';
import { shopsQuery } from '../../utils';
import { IShop } from '../../interfaces';
import { useShopTypes } from '../use-shop-types';
import { SettingsContext } from '@modules/settings';

export const useShopsTableColumns = () => {
  const history = useHistory();
  const location = useLocation();
  const { fetch } = React.useContext(ShopsTableContext);
  const settings = useContext(SettingsContext);
  const { data: shopTypes } = useShopTypes();
  const removeShopRepo = useService<IRemoveShopRepo>(RemoveShopRepoType);

  const actionsColumn = useMemo<Column<IShop>>(
    () => ({
      ...tableColumns.actions,
      Cell: ({ row: { original } }) => {
        const [visible, setVisible] = useState<boolean>(false);

        const openEdit = useCallback(() => {
          history.push(generatePath('/@next/shops/:id/update', { id: original.id }), { background: location });
        }, [original.id]);

        const openDetails = useCallback(() => {
          history.push(generatePath('/@next/shops/:id/details', { id: original.id }), { background: location });
        }, [original.id]);

        const remove = React.useCallback(() => {
          const onOk = async () => {
            const result = await removeShopRepo.execute([original.id]);

            if (result.status === 200) {
              fetch();
            } else {
              message.error(result.response);
            }
          };

          Modal.confirm({ title: 'Diqqət', content: 'Bu mağazanı silməyə əminsinizmi?', onOk });
        }, [original.id]);

        const overlay = (
          <Menu onClick={() => setVisible(false)}>
            <Menu.Item onClick={openEdit} icon={<Icons.EditOutlined />}>
              Düzəliş et
            </Menu.Item>
            <Menu.Item onClick={openDetails} icon={<Icons.FileTextOutlined />}>
              Ətraflı bax
            </Menu.Item>
            <Menu.Item onClick={remove} icon={<Icons.DeleteOutlined />}>
              Sil
            </Menu.Item>
          </Menu>
        );

        return (
          <StopPropagation>
            <Dropdown visible={visible} onVisibleChange={setVisible} overlay={overlay}>
              <Button icon={<Icons.MoreOutlined />} size='small' />
            </Dropdown>
          </StopPropagation>
        );
      },
    }),
    [history, location, removeShopRepo, fetch],
  );
  return useMemo<Column<IShop>[]>(
    () => [
      actionsColumn,
      {
        ...tableColumns.small,
        accessor: (row) => row.id,
        id: shopsQuery.id,
        Header: 'Kod',
      },
      {
        ...tableColumns.normal,
        accessor: (row) => row.logo,
        id: shopsQuery.logo,
        Header: 'Logo',
        Cell: ({ cell: { value } }) => {
          const onOpenShopImage = useCallback(
            (e: MouseEvent) => {
              e.stopPropagation();
              window.open(value);
            },
            [value],
          );
          return (
            <Tag color='green'>
              Keçid et&nbsp;
              <Icons.LinkOutlined onClick={onOpenShopImage} />
            </Tag>
          );
        },
      },
      {
        accessor: (row) => row.url,
        id: shopsQuery.url,
        Header: 'Link',
        Cell: OverCell,
      },
      {
        accessor: (row) => row.countryId,
        id: shopsQuery.countryId,
        Header: 'Ölkə',
        Cell: CountryCell,
        Filter: ({ column: { filterValue, setFilter } }) => {
          return (
            <Select showSearch={true} allowClear={true} style={{ width: '100%' }} onChange={setFilter} value={filterValue}>
              {settings &&
                settings.countries.map((country) => (
                  <Select.Option key={country.id} value={country.id.toString()}>
                    {country.name}
                  </Select.Option>
                ))}
            </Select>
          );
        },
      },
      {
        accessor: (row) => row.label,
        id: shopsQuery.shop_name,
        Header: 'Mağaza',
      },
      {
        ...tableColumns.normal,
        accessor: (row) => row.categoryName,
        id: shopsQuery.category_id,
        Header: 'Kateqoriya',
        Filter: ({ column: { filterValue, setFilter } }) => {
          return (
            <Select showSearch={true} allowClear={true} style={{ width: '100%' }} onChange={setFilter} value={filterValue}>
              {shopTypes &&
                shopTypes.map((type) => (
                  <Select.Option key={type.id} value={type.id.toString()}>
                    {type.name}
                  </Select.Option>
                ))}
            </Select>
          );
        },
      },
    ],
    [actionsColumn, shopTypes, settings],
  );
};
